import { Component, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import {
  MaintenanceService,
  MaintenanceConfig,
} from "src/app/services/maintenance.service";

type BannerPhase = "pre" | "active" | "complete";

@Component({
  selector: "app-maintenance-banner",
  templateUrl: "./maintenance-banner.component.html",
  styleUrls: ["./maintenance-banner.component.scss"],
})
export class MaintenanceBannerComponent implements OnInit, OnDestroy {
  showBanner = false;
  phase: BannerPhase = "pre";
  message = "";

  private autoCloseRef: any;
  private pollRef: any;
  private readonly SESSION_KEY_PREFIX = "maint_seen_";
  private readonly COMPLETION_SESSION_KEY_PREFIX = "maint_completion_seen_";
  private currentStartUtc: string | null = null;

  constructor(
    private renderer: Renderer2,
    private maintenanceService: MaintenanceService,
  ) { }

  ngOnInit(): void {
    // this.fetchConfig();
    // this.pollRef = setInterval(() => this.fetchConfig(), 5 * 60 * 1000);
  }

  private fetchConfig(): void {
    this.maintenanceService.getConfig().subscribe((cfg) => {
      if (!cfg || !cfg.is_enabled) {
        this.hideBanner();
        return;
      }
      this.evaluate(cfg);
    });
  }

  private evaluate(cfg: MaintenanceConfig): void {
    const now = new Date();
    const start = cfg.start_utc ? new Date(cfg.start_utc) : null;
    const end = cfg.end_utc ? new Date(cfg.end_utc) : null;

    if (!start || !end) {
      this.hideBanner();
      return;
    }

    const completionMinutes = cfg.auto_completion_message_time ?? 30;
    const completionEnd = new Date(
      end.getTime() + completionMinutes * 60 * 1000,
    );
    const showFrom = new Date(
      start.getTime() - cfg.days_before_show * 24 * 60 * 60 * 1000,
    );
    const isCompletePhase =
      now >= end && now < completionEnd && !!cfg.completion_message?.trim();

    if (now < showFrom || (now >= end && !isCompletePhase)) {
      this.hideBanner();
      return;
    }

    if (isCompletePhase) {
      if (this.wasCompletionSeenThisSession(cfg.start_utc!)) {
        this.hideBanner();
        return;
      }
      this.phase = "complete";
    } else {
      if (this.wasSeenThisSession(cfg.start_utc!)) {
        this.hideBanner();
        return;
      }
      this.phase = now >= start ? "active" : "pre";
    }

    this.currentStartUtc = cfg.start_utc;
    const template =
      this.phase === "complete"
        ? (cfg.completion_message ?? "")
        : this.phase === "active"
          ? (cfg.active_message ?? "")
          : (cfg.pre_message ?? "");
    this.message = this.buildMessage(template, start, end);
    this.showBanner = true;
    this.renderer.setStyle(document.body, "padding-top", "48px");

    clearTimeout(this.autoCloseRef);
    const autoCloseMs = cfg.auto_close_minutes * 60 * 1000;
    const remainingMs = isCompletePhase
      ? completionEnd.getTime() - now.getTime()
      : autoCloseMs;
    const dismissTimeout = isCompletePhase
      ? Math.min(autoCloseMs, remainingMs)
      : autoCloseMs;
    this.autoCloseRef = setTimeout(() => {
      if (isCompletePhase) {
        this.markCompletionSeenThisSession(cfg.start_utc!);
      } else {
        this.markSeenThisSession(cfg.start_utc!);
      }
      this.closeBanner();
    }, dismissTimeout);
  }

  private buildMessage(template: string, start: Date, end: Date): string {
    const datePart = start.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
    });
    const startPart = start.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    const endPart = end.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    const tzPart =
      start
        .toLocaleTimeString(undefined, { timeZoneName: "short" })
        .split(" ")
        .slice(-1)[0] ?? "";
    return (template || "")
      .replace("{date}", datePart)
      .replace("{start}", startPart)
      .replace("{end}", endPart)
      .replace("{timezone}", tzPart);
  }

  private wasSeenThisSession(startUtc: string): boolean {
    return !!sessionStorage.getItem(`${this.SESSION_KEY_PREFIX}${startUtc}`);
  }

  private markSeenThisSession(startUtc: string): void {
    sessionStorage.setItem(`${this.SESSION_KEY_PREFIX}${startUtc}`, "1");
  }

  closeBanner(): void {
    if (this.currentStartUtc) {
      if (this.phase === "complete") {
        this.markCompletionSeenThisSession(this.currentStartUtc);
      } else {
        this.markSeenThisSession(this.currentStartUtc);
      }
    }
    clearTimeout(this.autoCloseRef);
    this.hideBanner();
  }

  private wasCompletionSeenThisSession(startUtc: string): boolean {
    return !!sessionStorage.getItem(
      `${this.COMPLETION_SESSION_KEY_PREFIX}${startUtc}`,
    );
  }

  private markCompletionSeenThisSession(startUtc: string): void {
    sessionStorage.setItem(
      `${this.COMPLETION_SESSION_KEY_PREFIX}${startUtc}`,
      "1",
    );
  }

  private hideBanner(): void {
    this.showBanner = false;
    this.renderer.removeStyle(document.body, "padding-top");
  }

  ngOnDestroy(): void {
    clearTimeout(this.autoCloseRef);
    clearInterval(this.pollRef);
    this.renderer.removeStyle(document.body, "padding-top");
  }
}
